import FormInput from '../FormInput';
import DynamicParams from '../../UI/DynamicParams';
import { BOX_NUMBER_OPTIONS, DYNAMIC_PARAMS, MAX_CHAR_LIMIT_20, PRIORITY_BANNER_OPTIONS, SIZE_OPTIONS } from '../../../utils/constants';
import { LINK_URL_QUERY_PARAMS } from '../../../utils/constants';
import { today, labelMap } from '../../../utils/utils';

const PanelBanner = (props) => {
  const {
    values,
    setValues: action,
    enums = {
      deepLinking: [],
      productType: [],
      bannerBackgroundColors: [],
      bannerTemplates: [],
      ttlBackgroundColors: []
    }
  } = props;

  const filteredDLOptions = enums.deepLinking
    ? enums.deepLinking.filter((el) => el.id !== 'DL_CHIUDI')
    : [];

  /**
   * Handles the change event for the end date input field.
   * Dispatches an action to update the campaign end date.
   * 
   * @param {Event} event - The change event from the date input element
   * @param {string} event.target.value - The new date value from the input
   */
  const handleStartDateChange = (event) => {
    action('startCampaignDate', event.target.value);
  };

  /**
   * Normalizes the end campaign date by ensuring it's not before the start campaign date or today's date.
   * If the end date is invalid (before the minimum allowed date), it resets the end campaign date to an empty string.
   * 
   * @function normalizeEndDate
   * @returns {void} This function doesn't return a value, it performs side effects through the action function
   * 
   * @description
   * - Gets today's date in ISO format
   * - Extracts startCampaignDate and endCampaignDate from values
   * - Returns early if no endCampaignDate is set
   * - Determines minimum allowed date (startCampaignDate or today, whichever is available)
   * - Clears endCampaignDate if it's before the minimum allowed date
  */
  const normalizeStartDate = () => {
    const todayISO = today();
    const { startCampaignDate, endCampaignDate } = values;

    if (!startCampaignDate) return;

    const start = startCampaignDate;

    const isBeforeToday = start < todayISO;
    const isAfterEnd = endCampaignDate && start > endCampaignDate;

    if (isBeforeToday || isAfterEnd) {
      action('startCampaignDate', '');
    }
  };

  /**
   * Handles the change event for the end date input field.
   * Dispatches an action to update the campaign end date.
   * 
   * @param {Event} event - The change event from the date input element
   * @param {string} event.target.value - The new date value from the input
   */
  const handleEndDateChange = (event) => {
    action('endCampaignDate', event.target.value);
  };

  const normalizeEndDate = () => {
    const todayISO = today();
    const { startCampaignDate, endCampaignDate } = values;

    if (!endCampaignDate) return;

    const end = endCampaignDate;
    const minAllowed = startCampaignDate || todayISO;

    const isBeforeMin = end < minAllowed;

    if (isBeforeMin) {
      action('endCampaignDate', '');
    }
  };

  const resetCtaValues = () => {
    switch (values.primaryCTAtype) {
      case 'DEFAULT':
        values.buttonActionLabel = '';
        values.buttonActionOption = '';
        values.buttonDeeplinkingLabel = '';
        values.buttonDeeplinkingOption = '';
        values.linkUrl = '';
        values.linkLabel = '';
        break;
      case 'ACTION':
        values.buttonDeeplinkingLabel = '';
        values.buttonDeeplinkingOption = '';
        values.linkUrl = '';
        values.linkLabel = '';
        break;
      case 'DL':
        values.buttonActionLabel = '';
        values.buttonActionOption = '';
        values.linkUrl = '';
        values.linkLabel = '';
        break;
      case 'EXT_LINK':
        values.buttonActionLabel = '';
        values.buttonActionOption = '';
        values.buttonDeeplinkingLabel = '';
        values.buttonDeeplinkingOption = '';
        break;
      default:
        break;
    }
  };

  return (
    <form className="form-container">
      <div className="form-row">
        <FormInput
          type="text"
          name="titleBanner"
          values={values}
          action={action}
          maxLength={MAX_CHAR_LIMIT_20}
        />
      </div>

      <div className="form-row">
        <FormInput
          type="text"
          name="textBanner"
          values={values}
          action={action}
        />
      </div>

      <div className="form-row withparam">
        {[...Array(DYNAMIC_PARAMS)].map((el, i) => (
          <DynamicParams
            key={i + 1}
            fieldNameToModify="textBanner"
            index={i + 1}
            values={values}
            action={action}
            label="PAR"
            identificator="%%"
          />
        ))}
      </div>

      <div className="form-row form-row--double">
        <div className="input-wrapper">
          <label className="label" htmlFor="startCampaignDate">
            {labelMap['startCampaignDate']}
          </label>
          <input
            type="date"
            className="input"
            id="startCampaignDate"
            name="startCampaignDate"
            onChange={handleStartDateChange}
            onBlur={normalizeStartDate}
            value={values.startCampaignDate || ''}
            min={today()}
            max={values.endCampaignDate || undefined}
          />
        </div>

        <div className="input-wrapper">
          <label className="label" htmlFor="endCampaignDate">
            {labelMap['endCampaignDate']}
          </label>
          <input
            type="date"
            className="input"
            id="endCampaignDate"
            name="endCampaignDate"
            onChange={handleEndDateChange}
            onBlur={normalizeEndDate}
            value={values.endCampaignDate || ''}
            min={values.startCampaignDate || today()}
          />
        </div>
      </div>

      <div className="form-row">
        <FormInput
          type="text"
          name="imageBanner"
          values={values}
          action={action}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput
          type="select"
          name="backgroundColor"
          values={values}
          action={action}
          options={enums.bannerBackgroundColors ? enums.bannerBackgroundColors : []}
        />
        <FormInput
          type="select"
          name="sizeBox"
          values={values}
          action={action}
          options={SIZE_OPTIONS}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput // TIPOLOGIA CARD
          type="select"
          name="templateLogicType"
          values={values}
          action={action}
          options={enums.bannerTemplates ? enums.bannerTemplates : []}
        />
        <FormInput // TIPO DI CTA PRIMARIA
          type="select"
          defaultOption
          id="primaryCTAtype"
          name="primaryCTAtype"
          values={values}
          action={action}
          options={[
            { id: 'DL', description: 'Link interno' },
            { id: 'ACTION', description: 'Azione' },
            { id: 'EXT_LINK', description: 'Link Esterno' }
          ]}
          onChange={resetCtaValues()}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput // CTA PRIMARIA
          type="text"
          name="buttonDeeplinkingLabel"
          values={values}
          action={action}
          hide={values.primaryCTAtype !== 'DL'}
        />
        <FormInput // AZIONE PRIMARIA
          defaultOption
          type="select"
          name="buttonDeeplinkingOption"
          values={values}
          action={action}
          options={filteredDLOptions}
          hide={!values.buttonDeeplinkingLabel || values.primaryCTAtype !== 'DL'}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput
          type="text"
          name="buttonActionLabel"
          values={values}
          action={action}
          hide={values.primaryCTAtype !== 'ACTION'}
        />
        <FormInput
          type="select"
          defaultOption
          name="buttonActionOption"
          values={values}
          action={action}
          options={enums.actions ? enums.actions : []}
          hide={!values.buttonActionLabel}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput
          type="text"
          name="linkLabel"
          values={values}
          action={action}
          hide={values.primaryCTAtype !== 'EXT_LINK'}
        />
      </div>

      {Boolean(values.linkLabel) && (
        <div className="form-row">
          <FormInput
            type="text"
            name="linkUrl"
            values={values}
            action={action}
            hide={!Boolean(values.linkLabel)}
            tooltip="Esempio di url consentito: https://www.lipsum.com?sfmc_p1=%%sfmc_p1%%&sfmc_p2=%%sfmc_p2%%&sfmc_p3=%%sfmc_p3%%&sfmc_p4=%%sfmc_p4%%&sfmc_p5=%%sfmc_p5%%"
          />
        </div>
      )}

      {Boolean(values.linkLabel) && (
        <div className="form-row withparam queryparam">
          {[...Array(LINK_URL_QUERY_PARAMS.LENGTH)].map((el, i) => (
            <DynamicParams
              key={i + 1}
              fieldNameToModify="linkUrl"
              index={i + 1}
              values={values}
              action={action}
              label={false}
              customLabel={LINK_URL_QUERY_PARAMS.NAMES[i]}
              identificator="%%"
              initialSpace={false}
            />
          ))}
        </div>
      )}

      <div className="form-row form-row--double">
        <FormInput // CHECKBOX ICONA CHIUSURA
          type="checkbox"
          name="closable"
          values={values}
          action={action}
        />
        <FormInput // CHECKBOX C3
          type="checkbox"
          name="consentC3"
          values={values}
          action={action}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput // NUM BOX
          type="select"
          name="target"
          values={values}
          action={action}
          options={BOX_NUMBER_OPTIONS}
        />
        <FormInput // PRIORITA'
          type="select"
          name="priorityBanner"
          values={values}
          action={action}
          options={PRIORITY_BANNER_OPTIONS}
        />
      </div>

      <div className="form-row form-row">
        <FormInput
          type="multiselect"
          name="bannerChannelChoiceLabel"
          values={values}
          action={action}
          options={enums.appNames ? enums.appNames : []}
        />
      </div>

      <div className="journey-info-wrapper">
        <p className="journey-template-id">(*) Campo obbligatorio</p>
      </div>
    </form>
  );
};

export default PanelBanner;
